import React from 'react';
import PropTypes from 'prop-types';
import { ArrowRight, Check } from 'lucide-react';
import Card from '../../shared/Card';
import Hyperlink from '../../Hyperlink';
import './ContentCard.css';

/**
 * ContentCard — a full-width copy card: H2, paragraphs, an optional row of
 * check-marked features and an optional link, on the M2 card surface, inside
 * the layout gutters. Built for the editorial / SEO copy between product
 * sections of a listing; any brand can use it for any copy.
 *
 * Figma: OVER-LL `2699:17078` (the full-width «SEO Block» of the Editions
 * listing, desktop 1472 wide). No mobile frame — mobile stacks the features
 * and takes the mobile tokens. The narrow «SEO Block» tiles BETWEEN product
 * cards are ProductsGrid's `tiles` (ProductsGridTile), not this block.
 *
 * - Renders nothing without a title, copy or features.
 * - `body`: one string or one string per paragraph (the same shape as the
 *   ProductsGrid text tile); `features`: `[{ title, description? }]` (the same
 *   shape as ContentBannerV2).
 * - The link is a real `<a href>` (crawlers follow it); without an `href` it
 *   still runs `onClick` (SPA routing) and does not navigate.
 * - No vertical margin of its own — the page spaces it between its sections.
 */
const ContentCard = ({
  title,
  titleAs: Title = 'h2',
  body,
  features = [],
  linkLabel,
  href,
  onClick,
  className = '',
  ...rest
}) => {
  const paragraphs = body == null ? [] : [].concat(body).filter(Boolean);
  const list = (features || []).filter((feature) => feature?.title);
  if (!title && paragraphs.length === 0 && list.length === 0) return null;

  const handleLink = (e) => {
    if (!href) e.preventDefault();
    onClick?.(e);
  };

  return (
    <section className={['ds-content-card', className].filter(Boolean).join(' ')} {...rest}>
      <div className="ds-content-card__container">
        <Card className="ds-content-card__card">
          {title && <Title className="ds-content-card__title typo-h2">{title}</Title>}

          {paragraphs.length > 0 && (
            <div className="ds-content-card__body typo-body-lg">
              {paragraphs.map((text, i) => <p key={i}>{text}</p>)}
            </div>
          )}

          {list.length > 0 && (
            <ul className="ds-content-card__features">
              {list.map(({ title: featureTitle, description }) => (
                <li key={featureTitle} className="ds-content-card__feature">
                  <Check className="ds-content-card__check" size={16} aria-hidden="true" />
                  <div className="ds-content-card__feature-body">
                    <p className="ds-content-card__feature-title typo-h5">{featureTitle}</p>
                    {description && <p className="ds-content-card__feature-desc typo-body">{description}</p>}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {linkLabel && (
            <Hyperlink
              variant="regular"
              href={href || '#'}
              onClick={handleLink}
              iconRight={<ArrowRight size={20} />}
              showIconRight
              className="ds-content-card__link"
            >
              {linkLabel}
            </Hyperlink>
          )}
        </Card>
      </div>
    </section>
  );
};

ContentCard.propTypes = {
  /** Heading (H2 style, uppercase) */
  title: PropTypes.string,
  /** Heading element — h2 by default; h3 under a section's own h2 */
  titleAs: PropTypes.elementType,
  /** Copy: one string, or one string per paragraph */
  body: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  /** Check-marked features: a row on desktop, stacked on mobile */
  features: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
  })),
  /** Link text (e.g. "Läs mer"); no link without it */
  linkLabel: PropTypes.string,
  /** Link URL */
  href: PropTypes.string,
  /** Link click handler (runs with or without an href) */
  onClick: PropTypes.func,
  /** Additional CSS class on the section */
  className: PropTypes.string,
};

export default ContentCard;
